import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { gql } from 'graphql-request'
import { Plus, FlaskConical } from 'lucide-react'

import Spinner from '@components/Spinner'
import { Button } from '@/components/ui/button'
import Breadcrumbs from '@components/Breadcrumbs'
import Pagination from '@components/Pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Badge } from '@/components/ui/badge'
import SegmentedControl from '@components/SegmentedControl'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'

import { useQuery } from '@hooks/graphql'
import { useSubscription } from '../../hooks/subscription'

import NoResults from './NoResults'
import NewClassroomModal from './NewClassroomModal'
import NewClassroomButton from './NewClassroomButton'

const CLASSROOMS_QUERY = gql`
  query classrooms($archived: Boolean, $page: Int) {
    classroomsPaginated(archived: $archived, page: $page, perPage: 10) {
      nodes {
        id
        name
        archived
        research
        studentsCount
        collaboratorsCount
      }
      pagesCount
      nodesCount
    }
  }
`

const ClassroomList = () => {
  const [showClassroomModal, setShowClassroomModal] = useState(false)
  const { hasProFeatures } = useSubscription()
  const [searchParams, setSearchParams] = useSearchParams()

  const page = parseInt(searchParams.get('page')) || 1
  const archived = searchParams.get('archived') === 'true'

  const setPage = page => {
    setSearchParams({ page })
  }

  const setArchived = archived => {
    if (archived) {
      setSearchParams({ archived })
    } else {
      setSearchParams({})
    }
  }

  const { isLoading, data: { classroomsPaginated: { nodes: classrooms = [], pagesCount } = {} } = {} } = useQuery({
    queryKey: ['classrooms', archived, page],
    gqlQuery: CLASSROOMS_QUERY,
    variables: { archived, page: parseInt(page) }
  })

  return (
    <>
      <Breadcrumbs>
        <Breadcrumbs.Text label='Classrooms' />
      </Breadcrumbs>

      <div className='mx-5'>
        <h1 className='text-2xl font-bold my-4'>Classrooms</h1>

        <div className='mb-4 flex justify-between items-center shrink-0'>
          <div className='flex items-center space-x-2'>
            <SegmentedControl
              options={[
                { label: 'Active', value: false },
                { label: 'Archived', value: true }
              ]}
              value={archived}
              onChange={setArchived}
            />
          </div>

          <NewClassroomButton
            hasProFeatures={hasProFeatures}
            showModal={() => setShowClassroomModal(true)}
          />
        </div>

        <Choose>
          <When condition={isLoading}>
            <Spinner className='relative top-0 left-0 flex items-center justify-center w-full h-screen' />
          </When>

          <When condition={classrooms.length < 1}>
            <NoResults openModal={() => setShowClassroomModal(true)} />
          </When>

          <Otherwise>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='w-[32%]'>Name</TableHead>
                  <TableHead className='w-[32%]'>Students</TableHead>
                  <TableHead className='w-[32%]'>Collaborators</TableHead>
                  <TableHead className='w-[4%]' />
                </TableRow>
              </TableHeader>

              <TableBody>
                <For each='classroom' of={classrooms}>
                  <TableRow key={classroom.id}>
                    <TableCell>
                      <Link className='block' to={`/educators/classrooms/${classroom.id}`}>
                        {classroom.name}

                        <If condition={classroom.archived}>
                          <Badge variant='outline' className='ml-2'>Archived</Badge>
                        </If>

                        <If condition={classroom.research}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <FlaskConical size={16} id='research-icon' className='ml-2 inline-flex' />
                            </TooltipTrigger>
                            <TooltipContent>This is a research classroom</TooltipContent>
                          </Tooltip>
                        </If>
                      </Link>
                    </TableCell>

                    <TableCell>
                      <Link className='block' to={`/educators/classrooms/${classroom.id}`}>
                        {classroom.studentsCount}
                      </Link>
                    </TableCell>

                    <TableCell>
                      <Link className='block' to={`/educators/classrooms/${classroom.id}`}>
                        {classroom.collaboratorsCount}
                      </Link>
                    </TableCell>

                    <TableCell className='text-right'>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant='ghost' size='sm'>
                            <span className='sr-only'>Open menu</span>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem>
                            <Link to={`/educators/classrooms/${classroom.id}`}>View</Link>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                </For>
              </TableBody>
            </Table>

            <Pagination
              page={page}
              pagesCount={pagesCount}
              setPage={setPage}
            />
          </Otherwise>
        </Choose>
      </div>

      <NewClassroomModal
        isOpen={showClassroomModal}
        onClose={() => setShowClassroomModal(false)}
      />
    </>
  )
}

export default ClassroomList
